import { isValidWebAppLocale } from '~/utils/guards/isValidWebAppLocale';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import type { EntryWithLinkData, IContentPage } from '~~/lib/ContentfulService';
import { StatusCodes } from 'http-status-codes';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import { enhancedNavigateTo } from '~/utils/enhancedNavigateTo';
import { setCookie } from 'h3';
import { shouldSkipMiddleware } from '~/utils/routing/routeExclusions';

// Performance optimization: Caching for invalid locales and error pages
const invalidLocaleCache = new Set<string>();
const geoLocaleCache = new Map<string, { locale: string; timestamp: number }>();
const errorPageCache = new Map<
  string,
  { message: string; timestamp: number }
>();

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const GEO_CACHE_TTL = 30 * 60 * 1000; // 30 minutes for geo-detection
const MAX_CACHE_SIZE = 100;

/**
 * Get cached geo locale with TTL check
 */
function getCachedGeoLocale(): string | null {
  const cached = geoLocaleCache.get('current');
  if (cached && Date.now() - cached.timestamp < GEO_CACHE_TTL) {
    return cached.locale;
  }
  return null;
}

/**
 * Set cached geo locale
 */
function setCachedGeoLocale(locale: string): void {
  geoLocaleCache.set('current', { locale, timestamp: Date.now() });
}

/**
 * Get cached error page message with TTL check
 */
function getCachedErrorMessage(locale: string): string | null {
  const cached = errorPageCache.get(locale);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.message;
  }
  return null;
}

/**
 * Set cached error page message with memory management
 */
function setCachedErrorMessage(locale: string, message: string): void {
  // Memory management: clear oldest entries if cache is full
  if (errorPageCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = errorPageCache.keys().next().value;
    if (oldestKey) {
      errorPageCache.delete(oldestKey);
    }
  }
  errorPageCache.set(locale, { message, timestamp: Date.now() });
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (shouldSkipMiddleware(to.path)) {
    return;
  }

  const locale = to.params.locale as string;

  // Early return for valid locales (fast path)
  if (isValidWebAppLocale(locale)) {
    const event = useRequestEvent();
    if (import.meta.server && event) {
      setCookie(event, 'locale', locale, {
        secure: true,
        sameSite: 'none',
        httpOnly: true
      });
    }
    return;
  }

  // Special handling for China locale - only redirect if it's exactly /cn/ (homepage)
  if (locale === 'cn' && to.path === '/cn/') {
    return enhancedNavigateTo(
      {
        path: '/zh-cn/',
        query: to.query
      },
      {
        external: true
      }
    );
  }

  // Early return for known invalid locales (production optimization)
  if (import.meta.client && invalidLocaleCache.has(locale)) {
    throw createError({
      statusCode: StatusCodes.NOT_FOUND,
      statusMessage: 'Page not found'
    });
  }

  // For all other invalid locales, show 404 with cached geo-localized error message
  if (
    import.meta.client ||
    (import.meta.server && process.env.NODE_ENV !== 'test')
  ) {
    let detectedLocale;
    let errorMessage = 'Page not found';

    try {
      // Try to get cached geo locale first
      const cachedGeoLocale = getCachedGeoLocale();

      if (cachedGeoLocale) {
        detectedLocale = cachedGeoLocale;
      } else {
        // Only do geo-detection if not cached
        try {
          const store = useGeoinformationStore();
          detectedLocale = (await store.ensureRedirectLocale()) ?? 'en';
          setCachedGeoLocale(detectedLocale);
        } catch (geoError) {
          console.warn('Error detecting locale for 404 message:', geoError);
          detectedLocale = 'en';
          setCachedGeoLocale('en'); // Cache fallback
        }
      }

      // Try to get cached error message
      const cachedErrorMessage = getCachedErrorMessage(detectedLocale);

      if (cachedErrorMessage) {
        errorMessage = cachedErrorMessage;
      } else {
        // Only fetch error page if not cached
        try {
          const notFoundPage = await $fetch<EntryWithLinkData<IContentPage>>(
            '/api/contentful/errorPage',
            {
              ...DEFAULT_FETCH_OPTIONS,
              query: {
                locale: detectedLocale,
                statusCode: StatusCodes.NOT_FOUND
              }
            }
          );

          errorMessage =
            notFoundPage.entry?.fields?.seoTitle ||
            notFoundPage.entry?.fields?.teaserTitle ||
            'Page not found';

          setCachedErrorMessage(detectedLocale, errorMessage);
        } catch (apiError) {
          console.warn('Error fetching localized error page:', apiError);
          errorMessage = 'Page not found';
          setCachedErrorMessage(detectedLocale, 'Page not found'); // Cache fallback
        }
      }
    } catch (error) {
      console.warn('Error in locale validation:', error);
    }

    // Cache the invalid locale for future requests (client-side only)
    if (import.meta.client) {
      invalidLocaleCache.add(locale);

      // Memory management for invalid locale cache
      if (invalidLocaleCache.size > MAX_CACHE_SIZE) {
        invalidLocaleCache.clear(); // Simple cleanup strategy
      }
    }

    throw createError({
      statusCode: StatusCodes.NOT_FOUND,
      statusMessage: errorMessage
    });
  }

  // Fallback for test environment
  throw createError({
    statusCode: StatusCodes.NOT_FOUND,
    statusMessage: 'Page not found'
  });
});
